import React, {
  ReactNode, useContext, useEffect, useState,
} from 'react';

import {
  CrudFilters, CrudSort, CrudSorting, useInvalidate, useNavigation,
} from '@refinedev/core';
import { useTable } from '@refinedev/react-table';

import { ColumnDef } from '@tanstack/react-table';
import { ArrowLeftIcon, Button } from '@rrexpress/core';
import { isEmpty } from 'lodash';

import BluebirdLogo from '@/assets/images/bluebird-mini.svg';
import { BaseListContext } from '@/context/base-list-context';

import Collapse from '../collapse';
import Filters, { ListFilterOption } from '../filters';
import { ITableComponentActiveSort, PaginationControls, TableComponent } from './table';

export type ListHeaderAction = {
  type: 'create' | 'primary' | 'secondary' | 'tertiary' | 'text' | 'custom',
  label?: string,
  customRender?: ReactNode,
  onClick?: () => void,
};

export interface IResourceItem {
  id?: string;
  [key: string]: unknown;
}

export interface BaseListProps {
  resource: string;
  dataProviderName?: string;
  columns: ColumnDef<IResourceItem>[];
  title?: string;
  headerActions?: ListHeaderAction[];
  showSortFilter?: boolean;
  sortColumnFieldObj?: Record<string, string>,
  filter?: ListFilterOption;
  hasGoBack?: boolean;
  handleGoBack?: () => void;
  resourceFilter?: Record<string, any>;
  initialSorters?: CrudSort[];
}
const BaseList: React.FC<BaseListProps> = ({
  resource,
  dataProviderName,
  columns,
  title,
  headerActions,
  showSortFilter,
  sortColumnFieldObj,
  filter,
  hasGoBack,
  handleGoBack,
  resourceFilter,
  initialSorters,
}) => {
  const [activeSort, setActiveSort] = useState<ITableComponentActiveSort>(
    (showSortFilter ? {} : null) as ITableComponentActiveSort,
  );
  const baseListContext = useContext(BaseListContext);

  const invalidate = useInvalidate();
  // const navigate = useNavigate();
  const { create, goBack } = useNavigation();

  const {
    getHeaderGroups,
    getRowModel,
    getState,
    setPageIndex,
    getCanPreviousPage,
    getPageCount,
    getCanNextPage,
    nextPage,
    previousPage,
    setPageSize,
    refineCore: {
      setFilters,
      setSorters,
    },
  } = useTable<IResourceItem>({
    refineCoreProps: {
      resource,
      dataProviderName,
      ...(!isEmpty(initialSorters) ? {
        sorters: {
          initial: initialSorters
        }
      } : {}),
      ...(!isEmpty(resourceFilter)
        ? {
          filters: {
            permanent: Object.entries(resourceFilter).map((value) => ({
              field: value[0],
              operator: 'contains',
              value: value[1],
            })) as CrudFilters,
          },
          
        }
        : {}
      ),
    },
    columns,
  });

  const refreshTable = () => {
    invalidate({ resource, invalidates: ['list'] });
  };

  function generateRefineFilter(filterArg: Record<string, any>): CrudFilters {
    const refineFilter: CrudFilters = [];

    Object.entries(filterArg).forEach(
      ([field, value]) => {
        refineFilter.push({
          field,
          operator: 'eq',
          value,
        });
      },
    );

    return refineFilter;
  }

  function generateRefineSorter(activeSortArg: ITableComponentActiveSort): CrudSorting {
    return [{
      field: !isEmpty(sortColumnFieldObj)
        ? sortColumnFieldObj[activeSortArg?.columnName] || activeSortArg?.columnName
        : activeSortArg?.columnName,
      order: activeSortArg.sortType === 'ASC' ? 'asc' : 'desc',
    }];
  }

  useEffect(() => {
    if (baseListContext) {
      baseListContext.setBaseListContextVal({
        refreshTable: () => refreshTable(),
      });
    }
  }, []);

  return (
    <div className="p-6 bg-white rounded-xl">
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-3 items-center capitalize text-xl font-bold">
          {(hasGoBack || handleGoBack) && (
            <div className="hover:text-gray-400 cursor-pointer" onClick={() => (handleGoBack ? handleGoBack() : goBack())}>
              <ArrowLeftIcon />
            </div>
          )}
          <img className="w-8" src={BluebirdLogo} alt="" />
          <div>
            {(title || resource).replace(/-_/g, ' ')}
          </div>
        </div>
        <div className="flex gap-2 justify-items-end">
          {(headerActions || []).reverse().map((action, i) => {
            if (action.type === 'create') {
              return <Button variant="tertiary" key={i} onClick={() => (action.onClick ? action.onClick() : create(resource))}>{action.label || 'Tambah Baru'}</Button>;
            } if (action.type === 'custom') {
              return action.customRender;
            }
            return (
              <Button
                key={i}
                variant={action.type}
                onClick={action.onClick}
              >
                {action.label}
              </Button>
            );
          })}
        </div>
      </div>

      {filter && (
        <Collapse
          isOpen
          items={[
            {
              title: 'Filter',
              content: (
                <div className="mt-4">
                  <Filters
                    {...filter}
                    onSubmit={(data: Record<string, any>) => {
                      const modifiedFilter = filter?.onSubmit?.(data) || data;

                      const refineFilter = generateRefineFilter(modifiedFilter);
                      setFilters(refineFilter, 'replace');

                      return refineFilter;
                    }}
                  />
                </div>
              ),
            },
          ]}
        />
      )}

      <TableComponent
        getHeaderGroups={getHeaderGroups}
        getRowModel={getRowModel}
        activeSort={activeSort}
        onSortChange={(newSort) => {
          if (newSort.sortType !== 'DEFAULT') {
            const refineSorter = generateRefineSorter(newSort);
            setSorters(refineSorter);
          }

          setActiveSort(newSort);
        }}
      />
      <PaginationControls
        getState={getState}
        setPageIndex={setPageIndex}
        getCanPreviousPage={getCanPreviousPage}
        previousPage={previousPage}
        nextPage={nextPage}
        getCanNextPage={getCanNextPage}
        getPageCount={getPageCount}
        setPageSize={setPageSize}
        pageSizeOptions={[5, 10, 20, 30, 40, 50]}
      />
    </div>
  );
};

export default BaseList;
